
import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Database } from '@/integrations/supabase/types';

// Define a more flexible type that matches what we're actually using
type BlogPost = Partial<Database['public']['Tables']['blog_posts']['Row']> & {
  author?: {
    first_name?: string;
    last_name?: string;
  } | null;
  tags?: { id: string; name: string }[];
};

export function useRelatedPosts(currentPostId: string | null, tagIds: string[], limit = 3) {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(false);
  const fetchKey = useMemo(() => {
    if (!currentPostId || !tagIds || tagIds.length === 0) return '';
    const keyTags = Array.from(new Set(tagIds)).sort().join(',');
    return `${currentPostId}|${keyTags}|${limit}`;
  }, [currentPostId, limit, tagIds.join(',')]);
  useEffect(() => {
    if (!fetchKey) {
      setPosts([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    // Create abort controller for cleanup
    const abortController = new AbortController();
    // Safety timeout: don't block UI longer than 6s for non-critical content
    const timeoutId = setTimeout(() => {
      if (!abortController.signal.aborted) {
        try { abortController.abort(); } catch {}
        setLoading(false);
      }
    }, 6000);

    const fetchRelatedPosts = async () => {
      if (!currentPostId || tagIds.length === 0) {
        setLoading(false);
        return;
      }

      try {
        // Only proceed if not aborted
        if (abortController.signal.aborted) return;

        // STEP 1: Find post IDs that share any of the provided tags
        const { data: tagLinks, error: tagLinksError } = await supabase
          .from('blog_posts_tags')
          .select('blog_post_id')
          .in('tag_id', tagIds)
          .neq('blog_post_id', currentPostId)
          .limit(limit * 5);

        // Check if request was aborted before updating state
        if (abortController.signal.aborted) return;

        if (tagLinksError) throw tagLinksError;

        const postIds = Array.from(new Set((tagLinks || []).map((l: any) => l.blog_post_id))).slice(0, limit);

        if (postIds.length === 0) {
          setPosts([]);
          return;
        }

        // STEP 2: Fetch posts by IDs
        const { data: postsData, error: postsError } = await supabase
          .from('blog_posts')
          .select(`
            id,
            title,
            slug,
            created_at,
            excerpt,
            featured_image,
            author_id,
            view_count,
            tags:blog_posts_tags(tag_id(id, name))
          `)
          .eq('status', 'published')
          .in('id', postIds);

        if (abortController.signal.aborted) return;

        if (postsError) throw postsError;

        // Transform and sort by view_count desc
        const transformedPosts = (postsData || [])
          .sort((a: any, b: any) => (b.view_count || 0) - (a.view_count || 0))
          .map(post => ({
            ...post,
            tags: post.tags?.map((tag: any) => ({
              id: tag.tag_id?.id || '',
              name: tag.tag_id?.name || ''
            })) || []
          }));

        setPosts(transformedPosts as BlogPost[]);
      } catch (err) {
        // Only log error if not aborted
        if (!abortController.signal.aborted) {
          console.error('Error fetching related posts:', err);
        }
      } finally {
        // Only update loading state if not aborted
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchRelatedPosts();

    // Cleanup function to abort any in-flight requests
    return () => {
      clearTimeout(timeoutId);
      abortController.abort();
    };
  }, [fetchKey]); // Refetch only when key changes

  return { posts, loading };
}
